import { useState } from 'react'
import type { Medication } from '../../store/medicationsSlice'
import { EditMedicationModal } from './EditMedicationModal'

interface MedicationDetailHeaderProps {
  medication: Medication
  onUpdate: (data: Partial<Medication>) => Promise<void>
}

function getStockStatus(quantity: number, threshold: number): { label: string; className: string } {
  if (quantity === 0) {
    return { label: 'Out of Stock', className: 'bg-[#f0dede] text-status-error' }
  }
  if (quantity <= threshold) {
    return { label: 'Low Stock', className: 'bg-[#f0e8d0] text-status-warning' }
  }
  return { label: 'In Stock', className: 'bg-accent-subtle-bg text-status-success' }
}

export function MedicationDetailHeader({ medication, onUpdate }: MedicationDetailHeaderProps) {
  const [editOpen, setEditOpen] = useState(false)
  const stockStatus = getStockStatus(medication.stock_quantity, medication.reorder_threshold)

  const handleSave = async (data: Partial<Medication>) => {
    await onUpdate(data)
    setEditOpen(false)
  }

  return (
    <div data-testid="medication-detail-header">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex flex-col gap-2">
          <h1
            className="text-[16px] font-semibold text-text-primary"
            data-testid="medication-name"
          >
            {medication.name}
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              data-testid="medication-category-badge"
              className="inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium bg-[#e0e4e8] text-status-info"
            >
              {medication.category}
            </span>
            <span
              data-testid="medication-form-badge"
              className="inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium bg-border text-text-muted"
            >
              {medication.form}
            </span>
            {medication.controlled_substance && (
              <span
                data-testid="medication-controlled-badge"
                className="inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium bg-[#f0dede] text-status-error"
              >
                Controlled — Schedule {medication.dea_schedule}
              </span>
            )}
            <span
              data-testid="medication-stock-status"
              className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${stockStatus.className}`}
            >
              {stockStatus.label}
            </span>
          </div>
        </div>
        <button
          data-testid="edit-medication-button"
          onClick={() => setEditOpen(true)}
          className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
        >
          Edit Medication
        </button>
      </div>

      {editOpen && (
        <EditMedicationModal
          medication={medication}
          onSave={handleSave}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  )
}
